import { useEffect, useState, useRef } from 'react';
import { useFile } from '../../context/FileContext';
import {
  isValidGithubUrl,
  isValidGithubToken,
  extractGithubPath
} from '../../utils/validators';
import { parseGithubUrl } from '../../utils/githubUrlParser';
import { selectDefaultBranch } from '../../constants/branchDefaults';
import { getSavedUrls, saveUrl, removeUrl } from '../../utils/urlHistory';
import { getSavedToken, saveToken, clearToken } from '../../utils/tokenStorage';
import GitHubClient from '../../api/GitHubClient';
import { trackGitHubRepoLoaded, trackError } from '../../utils/analyticsEvents';

/**
 * GitHub connector component - handles repository URL and branch selection
 */
const GithubConnector = () => {
  const {
    githubLink,
    setGithubLink,
    githubBranch,
    setGithubBranch,
    setGithubNameAndRepo,
    githubAccessToken,
    setGithubAccessToken,
    loadFile,
    setFocusedFilePath
  } = useFile();
  const [branches, setBranches] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [savedUrls, setSavedUrls] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [rememberData, setRememberData] = useState(true);
  const [pendingFileToLoad, setPendingFileToLoad] = useState(null);
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const savedRemember = localStorage.getItem('github_remember_data');
    if (savedRemember !== null) {
      setRememberData(JSON.parse(savedRemember));
    }
    // Load saved URLs and token on mount
    setSavedUrls(getSavedUrls());
    const savedToken = getSavedToken();
    if (savedToken) {
      setGithubAccessToken(savedToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (githubLink === '' && githubAccessToken === '') return;

    setStatusMessage('');

    // Don't validate if URL is empty (user just started or only has token)
    if (githubLink === '') return;

    if (!isValidGithubUrl(githubLink)) {
      setStatusMessage('! Please enter a valid GitHub repository URL.');
      return;
    }

    if (!isValidGithubToken(githubAccessToken)) {
      setStatusMessage('! Please enter a valid GitHub access token.');
      return;
    }

    let handler = setTimeout(async () => {
      try {
        // Parse GitHub URL to extract repo and optionally branch/file
        const parsed = parseGithubUrl(githubLink);
        if (!parsed) {
          setStatusMessage('! Invalid GitHub URL format.');
          return;
        }

        // If branch was extracted from URL, use it
        if (parsed.branch) {
          setGithubBranch(parsed.branch);
        }

        // Use parsed ownerRepo for API calls
        setGithubNameAndRepo(parsed.ownerRepo);

        const branchNames = await getBranches(parsed.ownerRepo);
        if (branchNames && branchNames.length > 0) {
          // If no branch was set yet, auto-select one
          if (!parsed.branch) {
            autoSelectBranch(branchNames);
          }
          
          trackGitHubRepoLoaded();
          
          // If file path was in URL, handle expansion and loading
          if (parsed.filePath) {
            setFocusedFilePath(parsed.filePath);
            
            // Only queue for loading if it's a file (blob), not a folder (tree)
            if (parsed.type === 'blob') {
              const fileNode = {
                name: parsed.filePath.split('/').pop(),
                path: parsed.filePath,
                type: 'blob'
              };
              setPendingFileToLoad(fileNode);
            } else {
              setPendingFileToLoad(null);
            }
          }

          // Save base repository URL and token only if user opted in
          if (rememberData) {
            const baseRepoUrl = `https://github.com${parsed.ownerRepo}`;
            saveUrl(baseRepoUrl);
            setSavedUrls(getSavedUrls());
            saveToken(githubAccessToken);
          }
        }
      } catch (error) {
        setStatusMessage(`! Error: ${error.message}`);
        trackError('GitHub Load Error');
      }
    }, 500);

    return () => clearTimeout(handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubLink, githubAccessToken]);

  const getBranches = async (nameAndRepo = null) => {
    const repoPath = nameAndRepo || extractGithubPath(githubLink);

    const githubClient = new GitHubClient(githubAccessToken);
    const branchNames = await githubClient.getBranches(repoPath);
    setBranches(branchNames);
    setStatusMessage(`Found ${branchNames.length} branches.`);
    formRef.current?.requestSubmit();

    return branchNames;
  };

  const autoSelectBranch = (branchNames) => {
    const selected = selectDefaultBranch(branchNames);
    if (selected) {
      setGithubBranch(selected);
    }
  };

  const selectUrl = (url) => {
    setGithubLink(url);
    setShowDropdown(false);
  };

  const deleteUrl = (e, url) => {
    e.stopPropagation();
    removeUrl(url);
    setSavedUrls(getSavedUrls());
  };

  const deleteToken = () => {
    clearToken();
    setGithubAccessToken('');
  };

  const handleRememberDataChange = (e) => {
    const value = e.target.checked;
    setRememberData(value);
    localStorage.setItem('github_remember_data', JSON.stringify(value));
  };

  const handleClickOutside = (e) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target) &&
      !inputRef.current.contains(e.target)
    ) {
      setShowDropdown(false);
    }
  };

  // Load pending file after branches are loaded
  useEffect(() => {
    if (pendingFileToLoad && branches.length > 0) {
      loadFile(pendingFileToLoad);
      setPendingFileToLoad(null);
    }
  }, [branches, pendingFileToLoad, loadFile]);

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div>
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <h1>GitHub Repository URL</h1>
        <span>
          (paste a repository URL or a link to a specific file/folder to automatically expand and
          load it)
        </span>
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            id="github-repo-input"
            name="github-repo-input"
            className="github-repo-input"
            autoComplete="off"
            type="text"
            placeholder="https://github.com/<username>/<repo>..."
            value={githubLink}
            onChange={(e) => {
              setGithubLink(e.target.value);
            }}
            onFocus={() => {
              if (savedUrls.length > 0) {
                setShowDropdown(true);
              }
            }}
          />
          {showDropdown && savedUrls.length > 0 && (
            <div
              ref={dropdownRef}
              className="github-urls-dropdown"
            >
              {savedUrls.map((url, idx) => (
                <div
                  key={idx}
                  onClick={() => selectUrl(url)}
                  className="github-urls-item"
                >
                  <div className="github-urls-text">
                    {url}
                  </div>
                  <button
                    onClick={(e) => deleteUrl(e, url)}
                    className="github-urls-delete-btn"
                    title="Delete this URL"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <h2>GitHub Access Token</h2>
        <span>
          (optional - for private repositories and higher rate limits. GitHub &gt; Settings &gt;
          Developer settings &gt; create a new classic token)
        </span>
        <div style={{ position: 'relative' }}>
          <input
            className="github-repo-input"
            name="github-token-input"
            autoComplete="off"
            type="text"
            placeholder="gph_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={githubAccessToken}
            onChange={(e) => setGithubAccessToken(e.target.value)}
          />
          {githubAccessToken && (
            <button
              onClick={deleteToken}
              className="github-token-delete-btn"
              title="Delete saved token"
            >
              ✕
            </button>
          )}
        </div>
        <div className="github-remember-data-container">
          <input
            type="checkbox"
            id="github-remember-data"
            className="github-remember-data-checkbox"
            checked={rememberData}
            onChange={handleRememberDataChange}
          />
          <label htmlFor="github-remember-data">
            Remember this data locally in browser (URLs and token stored in your browser). Data are saved when repo succesfully loads.
          </label>
        </div>
        <p style={{ color: statusMessage.startsWith('!') ? 'red' : 'black' }}>
          {statusMessage}
        </p>
        <button type="submit" style={{ display: 'none' }}>
          Submit
        </button>
      </form>
      <h2>Branch</h2>
      <select
        id="github-branch-select"
        className="github-branch-select"
        value={githubBranch}
        onChange={(e) => setGithubBranch(e.target.value)}
      >
        {branches.map((branch) => (
          <option key={branch} value={branch}>
            {branch}
          </option>
        ))}
      </select>
    </div>
  );
};

export default GithubConnector;
